import React, { useState } from 'react'
import { Route, RouteComponentProps } from 'react-router-dom'
import { JustifiedGallery, GalleryLayoutMode } from '../JustifiedGallery'
import { DateScrubber } from '../DateScrubber'
import { OrderType, ResourceType, ResourcesOrdering } from '../../services/api'
import { isTimelineOrderBy } from '../GallerySort/gallerySortParams'
import { useGalleryView } from './GalleryView.hook'
import { useNearEndSentinel } from '../../utils/useNearEndSentinel'
import { FetchingLoader } from '../FetchingLoader'
import { LoaderBlock } from '../Spinner'
import i18next from 'i18next'
import { PhotoDisplayer } from '../PhotoDisplayer'
import { PageContent } from '../Common/PageContent'
import { SEARCH_QUERY_PARAM } from '../../constants/config'

const Route1 = Route as any

interface GalleryViewProps extends RouteComponentProps<any> {
  children?: React.ReactNode
  perPage?: number
  startPage?: number
  albumId?: string
  publicPhotos?: boolean
  query?: string
  tags?: string[]
  personIds?: string[]
  ownerIds?: string[]
  deviceId?: string
  placeId?: string
  type?: ResourceType
  dateFrom?: Date
  dateTo?: Date
  isFavorite?: boolean
  orderBy?: ResourcesOrdering
  order?: OrderType
  workspaceId: string
  workspaceSlug?: string
  /** default true; search results and non-date orderings turn this off */
  groupByDate?: boolean
  layoutMode?: GalleryLayoutMode
}

interface GalleryViewState {
  previewPhotoId: string
}

const GalleryView: React.FC<GalleryViewProps> = ({
  children,
  perPage,
  startPage,
  albumId,
  publicPhotos,
  query,
  tags,
  personIds,
  ownerIds,
  deviceId,
  placeId,
  type,
  dateFrom,
  dateTo,
  isFavorite,
  orderBy,
  order,
  workspaceId,
  workspaceSlug,
  groupByDate = true,
  layoutMode,
  history,
  match,
  location,
}) => {
  const [{ previewPhotoId }, _setState] = useState<GalleryViewState>({
    previewPhotoId: undefined,
  })
  const setState = (newState: Partial<GalleryViewState>): void =>
    _setState((oldState) => ({ ...oldState, ...newState }))

  const previewOpen = location.pathname.includes('/preview/')

  const gallery = useGalleryView({
    workspaceId,
    perPage,
    startPage,
    albumId,
    publicPhotos,
    query,
    tags,
    personIds,
    ownerIds,
    deviceId,
    placeId,
    type,
    dateFrom,
    dateTo,
    isFavorite,
    orderBy,
    order,
    previewOpen,
  })
  const { photos, isFetching } = gallery

  // day headers only make sense when the sort key is the item's `date`: search results are
  // relevance-ranked, and date_added_to_album sorts by an instant the item doesn't carry
  const showDayHeaders = groupByDate && !query && isTimelineOrderBy(orderBy ?? 'date_taken')

  const sentinelRef = useNearEndSentinel({
    onReached: gallery.onSentinelReached,
    disabled: gallery.sentinelDisabled,
  })

  let currentUrl: string = match.url

  if (currentUrl[currentUrl.length - 1] === '/') {
    currentUrl = currentUrl.substr(0, currentUrl.length - 1)
  }

  const hasPhotos = Boolean(photos.length)

  const openTag = (tag: string): void => {
    history.push(`/${workspaceSlug || workspaceId}?${SEARCH_QUERY_PARAM}=${encodeURIComponent(tag)}`)
  }

  const onPhotoPreviewClose = () => {
    history.goBack()
  }

  const onPhotoDeleted = () => {
    gallery.reset()
    onPhotoPreviewClose()
  }

  const onPhotoNavigate = (photoId: string, index: number) => {
    history.replace(`${currentUrl}/preview/${photoId}${location.search}`)
    setState({ previewPhotoId: photoId })
  }

  const onPhotoClick = React.useCallback(
    (photoId: string) => {
      history.push(`${currentUrl}/preview/${photoId}${location.search}`)
      setState({ previewPhotoId: photoId })
    },
    [currentUrl, location.search]
  )

  const isIndexLoaded = (flatIndex: number): boolean =>
    flatIndex >= gallery.windowStart && flatIndex < gallery.windowStart + photos.length

  return (
    <div className="min-h-0">
      <PageContent>
        {children}
        <JustifiedGallery
          photos={photos}
          onPhotoClick={onPhotoClick}
          groupByDate={showDayHeaders}
          layoutMode={layoutMode}
          onVisibleRangeChange={gallery.virtualMode ? gallery.onVisibleRangeChange : undefined}
          scrollTarget={gallery.galleryScrollTarget}
          onScrollTargetApplied={gallery.onScrollTargetApplied}
        />
        <div ref={sentinelRef} />
        {!hasPhotos && !isFetching && (
          <div className="pt-24 flex justify-center">
            <p className="text-gray-500">
              {i18next.t('photos.hereYouWillSeeYourPhotos')}
            </p>
          </div>
        )}
        {!hasPhotos && isFetching && <LoaderBlock label={i18next.t('loading.text')} />}
        {/* the pill is for appending to an existing list — the block already covers first load */}
        {isFetching && hasPhotos && <FetchingLoader />}
      </PageContent>
      {gallery.scrubber && !previewOpen && (
        <DateScrubber
          timeline={gallery.scrubber.timeline}
          fraction={gallery.scrubber.fraction}
          onJump={gallery.scrubber.onJump}
          isIndexLoaded={isIndexLoaded}
        />
      )}
      <Route1
        path={`${currentUrl}/preview/:photoId`}
        render={(routeProps) => (
          <PhotoDisplayer
            {...routeProps}
            onDelete={onPhotoDeleted}
            photoId={previewPhotoId}
            onTagClick={openTag}
            onClose={onPhotoPreviewClose}
            onNavigate={onPhotoNavigate}
            onNearEnd={gallery.onSentinelReached}
            onNearStart={gallery.onNearStart}
            countOffset={gallery.windowStart}
            totalCount={gallery.total}
            photos={photos}
          />
        )}
      />
    </div>
  )
}

export default GalleryView
